import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Complex Services Workflow API Functions
 *
 * Exact TS mirror of `farchat/common/complex_services_decision_rules.py`. The
 * two stay in lockstep via the golden fixture
 * `tests/fixtures/complex_stage_matrix.json` (repo root) — see
 * `src/api/__tests__/applicableComplexStages.test.ts`.
 *
 * Three branch axes compose (spec §5):
 *   - `acquisition_mode` — `new` vs `recompete` (diff old PWS).
 *   - `sat_threshold` — `under_sat` vs `over_sat` (derived from the IGE total vs
 *     $350K by `deriveSatThreshold`). Gates DD 2579 / reqval / QASP.
 *   - `pop_structure` — `one_time` vs `multi_year`. With `recompete` this drives
 *     the options stage.
 *
 * The master order (`COMPLEX_SERVICES_STAGE_ORDER`) is load-bearing: the reused
 * authoring keys (codes 600–606) come first, then the complex-only keys
 * (607–619) with the always-on `record` (609) LAST — it is the terminal
 * completion stage (spec §5.3/§5.6): a flow is complete iff 609 ∈
 * completedStages.
 *
 * The reused authoring stages 600–606 hit the existing `/api/services/*`
 * endpoints via `servicesFunctions.ts` — this module only adds client functions
 * for the complex-only stages 607–619 (plus record export + the applicable
 * -stages echo).
 */

import { apiCall } from './apiClient';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';

// --------------------------------------------------------------------------- //
// Branch vocabulary (mirrors farchat/models/complex_services_data.py).         //
// --------------------------------------------------------------------------- //

export type AcquisitionMode = 'new' | 'recompete';
export type SatThreshold = 'under_sat' | 'over_sat';
export type PopStructure = 'one_time' | 'multi_year';

/** Every complex-services stage key (mirrors the backend master order). */
export type ComplexStageKey =
  | 'sow_upload'
  | 'context_questions'
  | 'suggestions'
  | 'manual_edit'
  | 'salient_chars'
  | 'code_selection'
  | 'vendor_discovery'
  | 'mrr'
  | 'ige'
  | 'igd'
  | 'compliance'
  | 'pws_diff'
  | 'options'
  | 'reqval'
  | 'qasp'
  | 'dd2579'
  | 'form9'
  | 'solicitation'
  | 'clearance'
  | 'record';

// --------------------------------------------------------------------------- //
// Thresholds (mirror the Python constants).                                    //
// --------------------------------------------------------------------------- //

/** SAT boundary (USD). IGE total `>=` this is `over_sat` (spec §5.1). */
export const SAT_THRESHOLD_BOUNDARY = 350_000.0;

/** DD 2579 secondary dollar trigger (USD): `> $10K` non-set-aside also fires. */
export const DD2579_SECONDARY_TRIGGER = 10_000.0;

// --------------------------------------------------------------------------- //
// Stage keys / order / codes.                                                  //
// --------------------------------------------------------------------------- //

/**
 * The reused Services authoring stages (codes 600–606), always enabled for both
 * new requirements and recompetes.
 */
export const COMPLEX_AUTHORING_KEYS: ComplexStageKey[] = [
  'sow_upload', // 600
  'context_questions', // 601
  'suggestions', // 602
  'manual_edit', // 603
  'salient_chars', // 604
  'code_selection', // 605
  'vendor_discovery', // 606
];

/**
 * Canonical MASTER ORDER of every complex-services stage. Order is load-bearing
 * (it is the order the dynamic stepper renders). Reused authoring keys first
 * (600–606), then the package artifacts and complex-only keys. Storage codes
 * do not control this order: MRR follows vendor discovery, IGD follows IGE,
 * and Form 9 follows DD 2579. `record` (609) remains terminal.
 */
export const COMPLEX_SERVICES_STAGE_ORDER: ComplexStageKey[] = [
  // Reused authoring (600–606).
  'sow_upload',
  'context_questions',
  'suggestions',
  'manual_edit',
  'salient_chars',
  'code_selection',
  'vendor_discovery',
  // Package artifacts and complex-only stages. Record is LAST.
  'mrr', // 618 — reviewed market-research artifact
  'ige', // 616
  'igd', // 617 — inherently-governmental determination
  'compliance', // 607
  'pws_diff', // 610
  'options', // 611
  'reqval', // 612
  'qasp', // 613
  'dd2579', // 608
  'form9', // 619 — purchase request / funding artifact
  'solicitation', // 614
  'clearance', // 615
  'record', // 609 — TERMINAL (completion iff 609 saved)
];

/**
 * Stage-key → backend stage code. Reused authoring keys keep the 600-band so the
 * existing `/api/services/*` endpoints/components are used verbatim; complex-only
 * keys are 607–619.
 */
export const COMPLEX_SERVICES_STAGE_CODE: Record<ComplexStageKey, number> = {
  sow_upload: 600,
  context_questions: 601,
  suggestions: 602,
  manual_edit: 603,
  salient_chars: 604,
  code_selection: 605,
  vendor_discovery: 606,
  compliance: 607,
  dd2579: 608,
  record: 609,
  pws_diff: 610,
  options: 611,
  reqval: 612,
  qasp: 613,
  solicitation: 614,
  clearance: 615,
  ige: 616,
  igd: 617,
  mrr: 618,
  form9: 619,
};

/**
 * A Complex Services flow is terminal exactly when the backend has persisted
 * the Contract Record (stage 609). This stays intentionally independent of
 * branch gates and archival state: both active and archived flows carry the
 * same completed-stage metadata.
 */
export const isComplexServicesFlowComplete = (
  completedStages: readonly number[] | null | undefined,
): boolean => completedStages?.includes(COMPLEX_SERVICES_STAGE_CODE.record) ?? false;

/** Stages that are ALWAYS enabled regardless of branch (spec §5.3). */
export const COMPLEX_ALWAYS_ON_KEYS: ReadonlySet<ComplexStageKey> = new Set<ComplexStageKey>([
  'ige',
  'igd',
  'mrr',
  'form9',
  'compliance',
  'solicitation',
  'clearance',
  'record',
]);

/** Map a complex-services stage key to its backend stage code. */
export function complexFormStageCode(key: ComplexStageKey): number {
  return COMPLEX_SERVICES_STAGE_CODE[key];
}

// --------------------------------------------------------------------------- //
// Threshold derivation.                                                        //
// --------------------------------------------------------------------------- //

/**
 * Derive the SAT band from the IGE total vs $350K (spec §5.1).
 *   - `ige_total >= $350,000` → `"over_sat"`.
 *   - anything else → `"under_sat"`.
 *
 * Robust by design (never throws): `0` / negative / `null` / non-numeric / NaN
 * all fall through to the safe `"under_sat"` default.
 */
export function deriveSatThreshold(
  igeTotal: number | string | null | undefined,
): SatThreshold {
  const value = typeof igeTotal === 'number' ? igeTotal : Number(igeTotal);
  // Number(null) === 0, Number('') === 0, Number(undefined) === NaN, junk → NaN.
  if (igeTotal === null || igeTotal === undefined || igeTotal === '') {
    return 'under_sat';
  }
  if (Number.isNaN(value)) {
    return 'under_sat';
  }
  return value >= SAT_THRESHOLD_BOUNDARY ? 'over_sat' : 'under_sat';
}

// --------------------------------------------------------------------------- //
// Applicable-stage predicate (exact mirror of applicable_complex_stages).      //
// --------------------------------------------------------------------------- //

/**
 * Return the ORDERED subset of `COMPLEX_SERVICES_STAGE_ORDER` that applies.
 *
 * Enable rules (spec §5.3):
 *   - Always-on: `{mrr, ige, igd, form9, compliance, solicitation, clearance,
 *     record}`.
 *   - Reused authoring keys 600–606 are always enabled.
 *   - `recompete` → adds `pws_diff`.
 *   - `multi_year OR recompete` → adds `options`.
 *   - `over_sat` → adds `{dd2579, reqval, qasp}`. `dd2579` is ALSO enabled when
 *     `igeTotal > $10K AND not setAside` (independent of the SAT band).
 *
 * Pure / deterministic — never throws, never calls the network. The returned
 * list preserves `COMPLEX_SERVICES_STAGE_ORDER`.
 */
export function applicableComplexStages(
  mode: AcquisitionMode | string | null | undefined,
  threshold: SatThreshold | string | null | undefined,
  pop: PopStructure | string | null | undefined,
  setAside: boolean,
  igeTotal?: number | string | null,
): ComplexStageKey[] {
  const triggered = new Set<ComplexStageKey>(COMPLEX_ALWAYS_ON_KEYS);
  COMPLEX_AUTHORING_KEYS.forEach((k) => triggered.add(k)); // 600–606 always apply

  if (mode === 'recompete') {
    triggered.add('pws_diff'); // 610
  }

  if (pop === 'multi_year' || mode === 'recompete') {
    triggered.add('options'); // 611
  }

  if (threshold === 'over_sat') {
    triggered.add('dd2579'); // 608
    triggered.add('reqval'); // 612
    triggered.add('qasp'); // 613
  }

  // Secondary DD 2579 trigger: >$10K non-set-aside (independent of SAT band).
  if (!setAside && igeTotal !== undefined && igeTotal !== null && igeTotal !== '') {
    const value = typeof igeTotal === 'number' ? igeTotal : Number(igeTotal);
    if (!Number.isNaN(value) && value > DD2579_SECONDARY_TRIGGER) {
      triggered.add('dd2579');
    }
  }

  return COMPLEX_SERVICES_STAGE_ORDER.filter((key) => triggered.has(key));
}

// --------------------------------------------------------------------------- //
// Stepper: fixed length, greying, navigation over the master order.           //
//                                                                             //
// The complex-services stepper renders EVERY stage in the master order (UI     //
// index === position in `COMPLEX_SERVICES_STAGE_ORDER`). A stage is ENABLED    //
// iff its key is in the applicable set; otherwise it is greyed/non-clickable   //
// and navigation must SKIP it.                                                 //
// --------------------------------------------------------------------------- //

function toKeySet(
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): Set<ComplexStageKey> {
  return Array.isArray(applicableSet) ? new Set(applicableSet) : applicableSet;
}

/** Total length of the complex-services stepper (constant across all branches). */
export function complexTotalStageCount(): number {
  return COMPLEX_SERVICES_STAGE_ORDER.length;
}

/** Whether the stage at `index` (into the master order) is enabled for the buy. */
export function isComplexStageEnabled(
  index: number,
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): boolean {
  const key = COMPLEX_SERVICES_STAGE_ORDER[index];
  if (!key) return false;
  return toKeySet(applicableSet).has(key);
}

/**
 * The DISABLED (greyed) UI indices — master-order positions whose key is not in
 * the applicable set. Returned sorted ascending.
 */
export function complexDisabledStageIndices(
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): number[] {
  const set = toKeySet(applicableSet);
  const out: number[] = [];
  for (let i = 0; i < COMPLEX_SERVICES_STAGE_ORDER.length; i += 1) {
    if (!set.has(COMPLEX_SERVICES_STAGE_ORDER[i])) {
      out.push(i);
    }
  }
  return out;
}

/**
 * The SPECIFIC, user-facing reason a complex stage is greyed for the active buy
 * (spec §5.3). Returns `null` only when the stage IS applicable. Every greyable
 * stage has a concrete reason so `StageWrapper`'s generic disabled tooltip is
 * never shown for a complex stage:
 *   - `pws_diff` greyed on a `new` buy → "Only for recompetes".
 *   - `options` greyed on a one-time non-recompete buy → multi-year message.
 *   - `dd2579` / `reqval` / `qasp` greyed `under_sat` → SAT-threshold message.
 */
export function complexDisabledStageReason(
  key: ComplexStageKey,
  mode: AcquisitionMode | string | null | undefined,
  threshold: SatThreshold | string | null | undefined,
  pop: PopStructure | string | null | undefined,
  setAside: boolean,
  igeTotal?: number | string | null,
): string | null {
  if (applicableComplexStages(mode, threshold, pop, setAside, igeTotal).includes(key)) {
    return null;
  }
  switch (key) {
    case 'pws_diff':
      return 'Only for recompetes';
    case 'options':
      return 'Only for multi-year acquisitions';
    case 'dd2579':
    case 'reqval':
    case 'qasp':
      return 'Only required above the SAT ($350K)';
    default:
      return null;
  }
}

/**
 * Build a `{ stepperIndex → reason }` map for every DISABLED stage with a
 * specific reason. Pairs with `complexDisabledStageIndices` and feeds
 * `StageWrapper`'s `disabledStageReasons` prop.
 */
export function complexDisabledStageReasonMap(
  mode: AcquisitionMode | string | null | undefined,
  threshold: SatThreshold | string | null | undefined,
  pop: PopStructure | string | null | undefined,
  setAside: boolean,
  igeTotal?: number | string | null,
): Record<number, string> {
  const applicable = new Set<ComplexStageKey>(
    applicableComplexStages(mode, threshold, pop, setAside, igeTotal),
  );
  const out: Record<number, string> = {};
  for (let i = 0; i < COMPLEX_SERVICES_STAGE_ORDER.length; i += 1) {
    const key = COMPLEX_SERVICES_STAGE_ORDER[i];
    if (!applicable.has(key)) {
      const reason = complexDisabledStageReason(key, mode, threshold, pop, setAside, igeTotal);
      if (reason) {
        out[i] = reason;
      }
    }
  }
  return out;
}

/**
 * Next ENABLED stage index AT OR AFTER `from`, skipping disabled stages. Returns
 * `null` when there is no enabled stage at/after `from` (workflow complete).
 */
export function nextEnabledComplexStage(
  from: number,
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): number | null {
  const total = complexTotalStageCount();
  for (let i = Math.max(0, from); i < total; i += 1) {
    if (isComplexStageEnabled(i, applicableSet)) return i;
  }
  return null;
}

/**
 * Previous ENABLED stage index AT OR BEFORE `from`, skipping disabled stages.
 * Returns `null` when none exists.
 */
export function prevEnabledComplexStage(
  from: number,
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): number | null {
  const total = complexTotalStageCount();
  for (let i = Math.min(from, total - 1); i >= 0; i -= 1) {
    if (isComplexStageEnabled(i, applicableSet)) return i;
  }
  return null;
}

/**
 * The HIGHEST enabled stage index at or below `maxReached`. Used so a restored /
 * reachable index that lands on a greyed stage snaps to a real (enabled) one.
 */
export function lastEnabledComplexStageAtOrBelow(
  maxReached: number,
  applicableSet: Set<ComplexStageKey> | ComplexStageKey[],
): number {
  const total = complexTotalStageCount();
  const cap = Math.min(maxReached, total - 1);
  const prev = prevEnabledComplexStage(cap, applicableSet);
  return prev ?? 0;
}

// --------------------------------------------------------------------------- //
// Contract record (spec §11) — the stage-609 tracker hand-off row.             //
// --------------------------------------------------------------------------- //

export type ContractStatus =
  | 'Current/Ongoing Contract'
  | 'Prep for Close Out'
  | 'Recompete'
  | 'New Requirement'
  | 'Closed Out'
  | 'Cancelled';

export type OptionPeriod =
  | 'Base Year'
  | 'Option Year 1'
  | 'Option Year 2'
  | 'Option Year 3'
  | 'Option Year 4'
  | '6 Month Ext'
  | 'N/A';

export type RiskLevel = 'Low' | 'Med' | 'High';

/**
 * The client `Complex Services Tracker.xlsx` row emitted by stage 609 (spec §11).
 * Field ORDER matches the tracker export columns verbatim.
 */
export interface ContractRecord {
  contract_status: ContractStatus | string;
  contract_number: string;
  contract_name: string;
  co: string;
  cs: string;
  administrator_comments: string;
  days_remaining: number | null;
  total_award_value: number | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  customer: string;
  customer_poc: string;
  ktr_poc: string;
  option_period: OptionPeriod | string;
  pop_start: string | null;
  pop_end: string | null;
  acp_due: string | null;
  acp_sent: string | null;
  sixty_day_due: string | null;
  sixty_day_sent: string | null;
  thirty_day_due: string | null;
  thirty_day_sent: string | null;
  // Optional (Waypoint growth room, spec §4).
  funding_lines?: string | null;
  key_personnel?: string | null;
  cpars_ref?: string | null;
  qasp_ref?: string | null;
  risk?: RiskLevel | string | null;
}

/**
 * Foundation shape for the three durable acquisition-package stages. Their
 * components own field-level forms while this client preserves artifact state.
 */
export interface ComplexServicesArtifactStageData {
  artifact_ref?: unknown;
  artifact?: unknown;
  artifact_satisfied?: boolean;
  [key: string]: unknown;
}

// --------------------------------------------------------------------------- //
// Client functions for the complex-only stages (607–619).                      //
//                                                                             //
// Foundation stubs — Wave 2 fleshes out the payload shapes. Each endpoint has  //
// a POST (save) and GET (load) variant under                                   //
// `/api/complex-services/{flow_id}/{slug}` (spec §12). The reused authoring     //
// stages 600–606 use `servicesFunctions.ts` / `/api/services/*` instead.       //
// --------------------------------------------------------------------------- //

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Build an `Error` carrying the error-contract fields (analytics only — never
 * rendered; UI routes `err.message` through `toUserFacingError()`).
 */
function buildContractError(resp: {
  error?: string;
  error_code?: string;
  technical_detail?: string;
}): Error & { error_code?: string; technical_detail?: string } {
  const err = new Error(
    resp.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
  ) as Error & { error_code?: string; technical_detail?: string };
  err.error_code = resp.error_code;
  err.technical_detail = resp.technical_detail;
  return err;
}

/** POST a complex-only stage payload to `/api/complex-services/{flowId}/{slug}`. */
async function saveComplexStage<T = any>(
  flowId: string,
  slug: string,
  payload: unknown,
): Promise<T> {
  // The backend `ComplexStageSaveRequest` binds the stage payload from the
  // request body's `data` field (`_stage_save(..., request.data, ...)`). Wrap
  // the raw payload here — the single source of truth — so every stage's save
  // hits the wire contract `{ data: {...fields} }`. Callers pass the RAW
  // payload; they must NOT wrap it themselves (that would double-wrap to
  // `{ data: { data: ... } }` and persist a nested blob).
  const response = await apiCall<T>(
    `/api/complex-services/${flowId}/${slug}`,
    'POST',
    { data: payload },
  );
  if (!response.success) {
    throw buildContractError(response);
  }
  return response.data as T;
}

/** GET a complex-only stage's persisted payload. Returns `null` on 404/empty. */
async function getComplexStage<T = any>(
  flowId: string,
  slug: string,
): Promise<T | null> {
  const response = await apiCall<T>(
    `/api/complex-services/${flowId}/${slug}`,
    'GET',
  );
  if (!response.success) {
    return null;
  }
  return (response.data ?? null) as T | null;
}

// Compliance (607).
export const saveComplexServicesCompliance = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'compliance', payload);
export const getComplexServicesCompliance = (flowId: string) =>
  getComplexStage(flowId, 'compliance');

// DD 2579 (608).
export const saveComplexServicesDd2579 = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'dd2579', payload);
export const getComplexServicesDd2579 = (flowId: string) =>
  getComplexStage(flowId, 'dd2579');

// Record (609) — terminal.
export const saveComplexServicesRecord = (flowId: string, payload: Partial<ContractRecord>) =>
  saveComplexStage<ContractRecord>(flowId, 'record', payload);
export const getComplexServicesRecord = (flowId: string) =>
  getComplexStage<ContractRecord>(flowId, 'record');

// PWS diff (610).
export const saveComplexServicesPwsDiff = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'pws-diff', payload);
export const getComplexServicesPwsDiff = (flowId: string) =>
  getComplexStage(flowId, 'pws-diff');

// Options (611).
export const saveComplexServicesOptions = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'options', payload);
export const getComplexServicesOptions = (flowId: string) =>
  getComplexStage(flowId, 'options');

// Requirements validation (612).
export const saveComplexServicesReqval = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'reqval', payload);
export const getComplexServicesReqval = (flowId: string) =>
  getComplexStage(flowId, 'reqval');

// QASP (613).
export const saveComplexServicesQasp = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'qasp', payload);
export const getComplexServicesQasp = (flowId: string) =>
  getComplexStage(flowId, 'qasp');

// Solicitation (614).
export const saveComplexServicesSolicitation = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'solicitation', payload);
export const getComplexServicesSolicitation = (flowId: string) =>
  getComplexStage(flowId, 'solicitation');

// Clearance (615).
export const saveComplexServicesClearance = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'clearance', payload);
export const getComplexServicesClearance = (flowId: string) =>
  getComplexStage(flowId, 'clearance');

// IGE / IGCE (616).
export const saveComplexServicesIge = (flowId: string, payload: unknown) =>
  saveComplexStage(flowId, 'ige', payload);
export const getComplexServicesIge = (flowId: string) =>
  getComplexStage(flowId, 'ige');

// IGD checklist (617) — always required package artifact.
export const saveComplexServicesIgd = (
  flowId: string,
  payload: ComplexServicesArtifactStageData,
) => saveComplexStage<ComplexServicesArtifactStageData>(flowId, 'igd', payload);
export const getComplexServicesIgd = (flowId: string) =>
  getComplexStage<ComplexServicesArtifactStageData>(flowId, 'igd');

// Market Research Report (618) — always required package artifact.
export const saveComplexServicesMrr = (
  flowId: string,
  payload: ComplexServicesArtifactStageData,
) => saveComplexStage<ComplexServicesArtifactStageData>(flowId, 'mrr', payload);
export const getComplexServicesMrr = (flowId: string) =>
  getComplexStage<ComplexServicesArtifactStageData>(flowId, 'mrr');

// AF Form 9 / Purchase Request (619) — always required package artifact.
export const saveComplexServicesForm9 = (
  flowId: string,
  payload: ComplexServicesArtifactStageData,
) => saveComplexStage<ComplexServicesArtifactStageData>(flowId, 'form9', payload);
export const getComplexServicesForm9 = (flowId: string) =>
  getComplexStage<ComplexServicesArtifactStageData>(flowId, 'form9');

/**
 * Explicitly trigger the shared PWS autofill extractor for the IGE stage
 * (`POST /api/complex-services/{flowId}/ige/fill`). `useSavedWork` (default
 * true) reuses a fresh cached bundle; `false` forces a fresh extraction from
 * scratch. Returns the autofilled IGE output (same shape `getComplexServicesIge`
 * returns). Throws an `Error` carrying the error-contract fields on failure.
 */
export async function fillComplexServicesIge<T = any>(
  flowId: string,
  useSavedWork: boolean,
): Promise<T> {
  const response = await apiCall<T>(
    `/api/complex-services/${flowId}/ige/fill`,
    'POST',
    { use_saved_work: useSavedWork },
  );
  if (!response.success) {
    throw buildContractError(response);
  }
  return response.data as T;
}

/**
 * Explicitly autofill the stage-618 Market Research Report draft
 * (`POST /api/complex-services/{flowId}/mrr/fill`). This mutation is never
 * performed by stage GET or ordinary draft autosave.
 */
export async function fillComplexServicesMrr<T = any>(
  flowId: string,
  useSavedWork = true,
): Promise<T> {
  const response = await apiCall<T>(
    `/api/complex-services/${flowId}/mrr/fill`,
    'POST',
    { use_saved_work: useSavedWork },
  );
  if (!response.success) {
    throw buildContractError(response);
  }
  return response.data as T;
}

/**
 * Explicitly autofill the stage-619 AF Form 9 / purchase-request draft
 * (`POST /api/complex-services/{flowId}/form9/fill`). This mutation is never
 * performed by stage GET or ordinary draft autosave.
 */
export async function fillComplexServicesForm9<T = any>(
  flowId: string,
  useSavedWork = true,
): Promise<T> {
  const response = await apiCall<T>(
    `/api/complex-services/${flowId}/form9/fill`,
    'POST',
    { use_saved_work: useSavedWork },
  );
  if (!response.success) {
    throw buildContractError(response);
  }
  return response.data as T;
}

/**
 * Export the stage-609 contract record as a single-row CSV/xlsx
 * (`GET /api/complex-services/{flowId}/record/export`). Returns the raw `Blob`
 * so the caller can trigger a download.
 */
export async function exportComplexServicesRecord(flowId: string): Promise<Blob> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/complex-services/${flowId}/record/export`,
    { method: 'GET', credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error(USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  }
  return response.blob();
}

/** Render persisted legacy data without saving or starting an authoring agent. */
export async function downloadComplexServicesSavedExport(flowId: string, stage: 'ige' | 'dd2579'): Promise<void> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/complex-services/${encodeURIComponent(flowId)}/${stage}/export`,
    { method: 'GET', credentials: 'include' },
  );
  if (!response.ok) throw new Error(response.status === 422
    ? USER_FACING_ERROR_MESSAGES.saved_output_unavailable
    : USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/)?.[1]
    || (stage === 'ige' ? 'saved-ige.xlsx' : 'saved-dd2579.pdf');
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally { window.URL.revokeObjectURL(url); }
}

/** Server echo of the applicable-stages decision (the authoritative source). */
export async function applicableComplexStagesRemote(args: {
  flowId?: string | null;
  mode: AcquisitionMode | null;
  threshold: SatThreshold | null;
  pop: PopStructure | null;
  setAside: boolean;
  igeTotal?: number | null;
}): Promise<{ stages: ComplexStageKey[]; codes: number[] }> {
  const response = await apiCall<{ stages?: string[]; codes?: number[] }>(
    '/api/complex-services/applicable-stages',
    'POST',
    {
      flow_id: args.flowId ?? null,
      acquisition_mode: args.mode ?? null,
      sat_threshold: args.threshold ?? null,
      pop_structure: args.pop ?? null,
      set_aside: args.setAside ?? false,
      ige_total: args.igeTotal ?? null,
    },
  );
  if (!response.success) {
    throw buildContractError(response);
  }
  const data = (response.data as { stages?: string[]; codes?: number[] }) ?? {};
  return {
    stages: Array.isArray(data.stages) ? (data.stages as ComplexStageKey[]) : [],
    codes: Array.isArray(data.codes) ? data.codes : [],
  };
}
